import { API_BASE, forgetSession, getToken, restoreSession } from './auth';

// API_BASE lives in ./auth (empty = same origin, riding Vite's dev proxy
// locally); set VITE_API_BASE_URL only when the frontend and API are deployed
// to different origins.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
  }
}

// The 15-minute staff access-token TTL means a 401 mid-session is routine,
// not necessarily an expired session — refresh once and retry before giving
// up. restoreSession() shares one in-flight refresh across every caller (see
// ./auth), so concurrent 401s can't race the rotating refresh cookie.

const ACTION_401_DETAILS = new Set(['invalid_credentials', 'mfa_required', 'mfa_invalid_code']);

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  isRetry = false,
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });

  if (resp.status === 401) {
    // Some 401s answer the action, not the session: a wrong password or MFA
    // code while changing a setting. Those must reach the caller as errors,
    // not refresh-and-sign-out.
    const detail = await resp.clone().json()
      .then((b: Record<string, unknown>) => b['detail'])
      .catch(() => undefined);
    if (typeof detail === 'string' && ACTION_401_DETAILS.has(detail)) {
      throw new ApiError(401, detail);
    }
    if (!isRetry && await restoreSession()) {
      return apiFetch<T>(path, init, true);
    }
    forgetSession();
    window.location.href = '/login';
    throw new ApiError(401, 'Session expired');
  }

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({})) as Record<string, unknown>;
    throw new ApiError(resp.status, (body['detail'] as string | undefined) ?? 'Request failed');
  }

  if (resp.status === 204) return undefined as unknown as T;
  return resp.json() as Promise<T>;
}
